import clsx from 'clsx';
import { Plus, Check, Trash } from '@phosphor-icons/react';
import type { Scene, SceneImage, SceneVideo } from '@/types';
import { MediaImage } from '@/components/MediaImage';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';

interface Props {
  scene: Scene;
  episodeUuid: string;
  onClick?: (scene: Scene) => void;
  onShiftClick?: (scene: Scene) => void;
  onDelete?: () => void;
  /** When false, this cell is dimmed because another scene is active. */
  isInLinkedSet: boolean;
  isLinked: boolean;
  isActive: boolean;
  hasActiveScene: boolean;
}

export function SceneCell({
  scene,
  episodeUuid,
  onClick,
  onShiftClick,
  onDelete,
  isLinked,
  isActive,
  hasActiveScene,
}: Props) {
  const images = useEpisodeStore((s) => s.images);
  const videos = useEpisodeStore((s) => s.videos);
  const toggleRef = useSelectionStore((s) => s.toggleReferenceScene);

  const sceneImages = scene.imageIds
    .map((id) => images[id])
    .filter((x): x is SceneImage => Boolean(x) && !x.deletedAt);
  const sceneVideos = scene.videoIds
    .map((id) => videos[id])
    .filter((x): x is SceneVideo => Boolean(x) && !x.deletedAt);

  const hero = scene.heroImageId
    ? sceneImages.find((img) => img.id === scene.heroImageId) ?? sceneImages.at(-1)
    : sceneImages.at(-1);

  const heroVideo = sceneVideos.at(-1);
  const showVideoBadge = heroVideo && heroVideo.status === 'ready';

  // Dim non-linked cells when another scene is active (but never dim the active one)
  const isDimmed = hasActiveScene && !isActive && !isLinked;

  function handleClick(e: React.MouseEvent) {
    if (e.shiftKey && onShiftClick) {
      e.preventDefault();
      onShiftClick(scene);
      return;
    }
    onClick?.(scene);
  }

  // Link-toggle handle is hidden on the active scene itself (can't reference self)
  const showLinkHandle = hasActiveScene && !isActive;

  return (
    <div
      className={clsx(
        'group relative aspect-[16/9] w-full overflow-hidden rounded-lg',
        'transition-all duration-base ease-out-soft',
        !hero && 'border border-dashed border-line-2 bg-surface-2',
        hero && 'bg-surface-inset',
        isDimmed && 'opacity-55',
        isActive && 'ring-1 ring-accent',
        isLinked && 'scale-[1.02] ring-2 ring-accent',
      )}
    >
      <button
        type="button"
        onClick={handleClick}
        className="block h-full w-full text-left"
        aria-label={`Scene ${scene.order}${scene.prompt ? `: ${scene.prompt}` : ''}`}
      >
        {/* Scene number — top-left */}
        <span
          className={clsx(
            'absolute left-2 top-2 z-10 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[11px] tabular-nums backdrop-blur-md',
            isLinked || isActive ? 'text-accent' : 'text-text-1',
          )}
        >
          {String(scene.order).padStart(2, '0')}
          {isActive && (
            <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" />
          )}
        </span>

        {/* Status indicators */}
        {scene.status === 'generating' && (
          <span className="absolute left-2 bottom-2 z-10 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-accent backdrop-blur-md">
            generating…
          </span>
        )}
        {scene.status === 'error' && (
          <span className="absolute left-2 bottom-2 z-10 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-err backdrop-blur-md">
            failed
          </span>
        )}

        {/* Linked-set tint over image */}
        {isLinked && hero && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1] bg-accent-soft"
          />
        )}

        {/* Body */}
        {hero ? (
          <ImageBody
            hero={hero}
            episodeUuid={episodeUuid}
            variantCount={sceneImages.length}
            showVideoBadge={Boolean(showVideoBadge)}
            videoCount={sceneVideos.length}
          />
        ) : (
          <EmptyBody prompt={scene.prompt} />
        )}
      </button>

      {/* Delete handle — bottom-right, hover-revealed. Hidden while another
          scene is active so the link-toggle handle has visual priority. */}
      {onDelete && !hasActiveScene && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Delete scene ${scene.order}`}
          className="absolute bottom-2 right-2 z-20 flex h-7 w-7 items-center justify-center rounded-sm bg-surface-3/80 text-text-2 opacity-0 backdrop-blur-md transition-all duration-fast hover:bg-err/20 hover:text-err group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Trash size={12} />
        </button>
      )}

      {/* Link-toggle handle — top-right corner, only when another scene is active */}
      {showLinkHandle && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleRef(scene.id);
          }}
          aria-label={isLinked ? `Remove Scene ${scene.order} from references` : `Add Scene ${scene.order} as reference`}
          className={clsx(
            'absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-sm backdrop-blur-md transition-all duration-fast',
            isLinked
              ? 'bg-accent text-text-on-accent shadow-elev-focus'
              : 'bg-surface-3/70 text-text-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-accent',
          )}
        >
          {isLinked ? <Check size={12} weight="bold" /> : <Plus size={12} weight="bold" />}
        </button>
      )}
    </div>
  );
}

function EmptyBody({ prompt }: { prompt: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-4 text-center">
      {prompt ? (
        <p className="line-clamp-3 text-[12px] italic text-text-2">{prompt}</p>
      ) : (
        <Plus size={20} className="text-text-4" />
      )}
    </div>
  );
}

function ImageBody({
  hero,
  episodeUuid,
  variantCount,
  showVideoBadge,
  videoCount,
}: {
  hero: SceneImage;
  episodeUuid: string;
  variantCount: number;
  showVideoBadge: boolean;
  videoCount: number;
}) {
  return (
    <>
      <MediaImage
        episodeUuid={episodeUuid}
        path={hero.path}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-base ease-out-soft group-hover:scale-[1.02]"
      />
      {variantCount > 1 && (
        <span className="absolute right-2 bottom-2 z-10 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-text-1 backdrop-blur-md">
          1/{variantCount}
        </span>
      )}
      {showVideoBadge && (
        <span className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-sm bg-accent/90 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-text-on-accent">
          ▶ {videoCount}
        </span>
      )}
    </>
  );
}
